(() => {
	'use strict';

	const selectors = {
		newsletterForm: '[data-newsletter-form]',
		newsletterHeading: '[data-newsletter-heading]',
		successMessage: '.newsletter__message--success',
		errorMessage: '.newsletter__message--error',
		popupTextParts: '.modal__subheading, .modal__title, .modal__description',
	};

	const classes = {
		success: 'has-success',
		error: 'has-error',
		hidden: 'hidden',
	};

	const attributes = {
		loading: 'aria-busy',
		preserveScroll: 'data-newsletter-preserve-scroll',
	};

	const storageKeys = {
		formId: 'newsletter_form_id',
		scrollY: 'newsletter_scroll_y',
	};

	class NewsletterComponent extends HTMLElement {
		constructor() {
			super();
			this.newsletter = this.querySelector(selectors.newsletterForm);
			this.sessionStorage = window.sessionStorage;
			this.stopSubmit = true;
			this.formID = null;
			this.ajaxEnabled = false;
		}

		connectedCallback() {
			if (window.location.pathname === '/challenge' || !this.newsletter) {
				return;
			}

			this.ajaxEnabled = this.newsletter.getAttribute('data-newsletter-ajax') === 'true';
			this.newsletterSubmit = (event) => (this.ajaxEnabled ? this.newsletterSubmitAjaxEvent(event) : this.newsletterSubmitEvent(event));
			this.newsletter.addEventListener('submit', this.newsletterSubmit);

			this.togglePopupSuccessState(this.newsletter.classList.contains(classes.success));

			if (!this.ajaxEnabled) {
				this.showMessage();
			}
		}

		newsletterSubmitEvent(event) {
			if (!this.stopSubmit) return;

			event.preventDefault();
			event.stopImmediatePropagation();

			this.storeScroll();
			this.removeStorage();
			this.writeStorage();

			this.stopSubmit = false;
			this.newsletter.submit();
		}

		newsletterSubmitAjaxEvent(event) {
			event.preventDefault();
			event.stopImmediatePropagation();

			this.setLoading(true);
			this.clearMessages();

			const payload = new URLSearchParams(new FormData(this.newsletter));
			if (!payload.has('form_type')) {
				payload.set('form_type', 'customer');
			}

			fetch(this.newsletter.action, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
					Accept: 'text/html',
				},
				body: payload.toString(),
				credentials: 'same-origin',
			})
				.then((response) => response.text().then((text) => ({ response, text })))
				.then(({ response, text }) => {
					const result = this.parseResponse(text);
					if (result.success) {
						this.showSuccess();
						return;
					}

					if (result.error) {
						this.showError(result.errorMessage);
						return;
					}

					if (response.ok) {
						this.showSuccess();
						return;
					}

					this.showError(result.errorMessage);
				})
				.catch(() => {
					this.showError();
				})
				.finally(() => {
					this.setLoading(false);
				});
		}

		parseResponse(text) {
			const result = {
				success: false,
				error: false,
				errorMessage: '',
			};

			if (!text) {
				return result;
			}

			const parsed = new DOMParser().parseFromString(text, 'text/html');
			const form = parsed.getElementById(this.newsletter.id);

			if (form) {
				result.success = form.classList.contains(classes.success);
				result.error = form.classList.contains(classes.error);

				const errorMessage = form.querySelector(selectors.errorMessage);
				if (errorMessage) {
					result.errorMessage = errorMessage.textContent.trim();
				}
			}

			if (!result.errorMessage) {
				result.errorMessage = this.extractErrorMessage(text);
			}

			return result;
		}

		extractErrorMessage(text) {
			if (!text) {
				return '';
			}

			const match = text.match(/This customer is already subscribed!?/i);
			return match ? match[0] : '';
		}

		setLoading(isLoading) {
			const submitButton = this.newsletter.querySelector('button[type="submit"]');
			this.newsletter.setAttribute(attributes.loading, isLoading ? 'true' : 'false');

			if (submitButton) {
				submitButton.disabled = isLoading;
			}
		}

		clearMessages() {
			const successMessage = this.getSuccessMessage();
			const errorMessage = this.getErrorMessage();

			this.newsletter.classList.remove(classes.success, classes.error);
			this.togglePopupSuccessState(false);

			if (successMessage) {
				successMessage.classList.add(classes.hidden);
				successMessage.setAttribute('hidden', 'hidden');
			}

			if (errorMessage) {
				errorMessage.classList.remove(classes.hidden);
			}
		}

		getSuccessMessage() {
			return this.newsletter.querySelector(selectors.successMessage);
		}

		getErrorMessage() {
			return this.newsletter.querySelector(selectors.errorMessage);
		}

		showSuccess() {
			const successMessage = this.getSuccessMessage();
			const errorMessage = this.getErrorMessage();

			this.newsletter.classList.remove(classes.error);
			this.newsletter.classList.add(classes.success);
			this.togglePopupSuccessState(true);

			if (successMessage) {
				successMessage.classList.remove(classes.hidden);
				successMessage.removeAttribute('hidden');
			}

			if (errorMessage) {
				errorMessage.classList.add(classes.hidden);
			}
		}

		showError(errorMessageText) {
			const errorMessage = this.getErrorMessage();

			this.newsletter.classList.remove(classes.success);
			this.newsletter.classList.add(classes.error);
			this.togglePopupSuccessState(false);

			if (errorMessage) {
				if (errorMessageText) {
					errorMessage.textContent = errorMessageText;
				}
				errorMessage.classList.remove(classes.hidden);
			}
		}

		togglePopupSuccessState(isSuccess) {
			const modalInner = this.newsletter.closest('.modal__inner');
			if (!modalInner) return;

			modalInner.classList.toggle('newsletter-success-active', !!isSuccess);

			const popupTextParts = modalInner.querySelectorAll(selectors.popupTextParts);
			popupTextParts.forEach((element) => {
				if (isSuccess) {
					element.setAttribute('hidden', 'hidden');
					element.style.display = 'none';
				} else {
					element.removeAttribute('hidden');
					element.style.display = '';
				}
			});
		}

		shouldPreserveScroll() {
			return !!(this.sessionStorage && this.newsletter.hasAttribute(attributes.preserveScroll));
		}

		storeScroll() {
			if (!this.shouldPreserveScroll()) {
				return;
			}

			const scrollY = window.scrollY || window.pageYOffset || 0;
			this.sessionStorage.setItem(storageKeys.scrollY, `${scrollY}`);
		}

		restoreScroll() {
			if (!this.shouldPreserveScroll()) {
				return;
			}

			const stored = this.sessionStorage.getItem(storageKeys.scrollY);
			if (stored !== null) {
				const parsed = parseInt(stored, 10);
				if (!isNaN(parsed)) {
					window.scrollTo(0, parsed);
				}
				this.sessionStorage.removeItem(storageKeys.scrollY);
			}

			this.removeHash();
		}

		removeHash() {
			if (window.location.hash) {
				history.replaceState(null, document.title, window.location.pathname + window.location.search);
			}
		}

		writeStorage() {
			if (this.sessionStorage !== undefined) {
				this.sessionStorage.setItem(storageKeys.formId, this.newsletter.id);
			}
		}

		readStorage() {
			this.formID = this.sessionStorage.getItem(storageKeys.formId);
		}

		removeStorage() {
			this.sessionStorage.removeItem(storageKeys.formId);
		}

		showMessage() {
			this.readStorage();
			if (this.newsletter.id !== this.formID) {
				return;
			}

			const form = document.getElementById(this.formID);
			const heading = form.parentElement.querySelector(selectors.newsletterHeading);
			const postedSuccessfully = window.location.search.indexOf('?customer_posted=true') !== -1;
			const hasError = window.location.search.indexOf('accepts_marketing') !== -1;

			if (postedSuccessfully) {
				form.classList.remove(classes.error);
				form.classList.add(classes.success);
				this.togglePopupSuccessState(true);
				if (heading) {
					heading.classList.add(classes.hidden);
					form.classList.remove(classes.hidden);
				}
			} else if (hasError) {
				form.classList.remove(classes.success);
				form.classList.add(classes.error);
				this.togglePopupSuccessState(false);
				if (heading) {
					heading.classList.add(classes.hidden);
					form.classList.remove(classes.hidden);
				}
			}

			if (postedSuccessfully || hasError) {
				window.addEventListener('load', () => {
					if (this.shouldPreserveScroll()) {
						this.restoreScroll();
						return;
					}

					this.scrollToForm(form);
				});
			}
		}

		scrollToForm(form) {
			const formRectangle = form.getBoundingClientRect();
			const inView =
				formRectangle.top >= 0 &&
				formRectangle.left >= 0 &&
				formRectangle.bottom <= window.theme.getWindowHeight() &&
				formRectangle.right <= window.theme.getWindowWidth();

			if (!inView) {
				setTimeout(() => {
					window.theme.scrollTo(form.getBoundingClientRect().top);
				}, 500);
			}
		}
	}

	if (!customElements.get('newsletter-component')) {
		customElements.define('newsletter-component', NewsletterComponent);
	}
})();
